import { useShopI18n } from '../../i18n'
import type { RuntimeHomeSettings } from '../hooks/runtime/cmsApi'
import { resolveLinkLabel, isExternalHref } from './home-fallback/footerLinks'
import { SocialIcon } from './home-fallback/SocialIcon'

type Props = {
  homeSettings: RuntimeHomeSettings
}

const normalizeSocialLabel = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]/g, '')

const HomeFallbackFooter = ({ homeSettings }: Props) => {
  const { locale, language, toLocalizedPath } = useShopI18n()
  const hasContent = Boolean(
    homeSettings.footerNote ||
      homeSettings.footerLinks.length ||
      homeSettings.socialLinks.length ||
      homeSettings.contactEmail ||
      homeSettings.contactPhone,
  )
  if (!hasContent) {
    return null
  }
  const currentYear = new Date().getFullYear()
  const shopName = homeSettings.shopName || 'Ecommerce'

  return (
    <footer className="ca-theme-transition relative left-1/2 w-screen -translate-x-1/2 overflow-hidden border-t border-(--color-border-default) bg-(--color-bg-card) text-(--color-text-secondary)">
      <div className="mx-auto grid w-full max-w-[1740px] gap-x-8 gap-y-6 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,0.95fr)_minmax(0,0.95fr)] lg:items-start lg:px-10">
        <section className="min-w-0 space-y-3 text-left">
          <a
            href={toLocalizedPath('/')}
            className="ca-theme-transition inline-flex items-center gap-3 hover:text-(--color-text-primary)"
          >
            {homeSettings.logoUrl ? (
              <img
                src={homeSettings.logoUrl}
                alt={shopName}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="h-10 w-10 rounded-lg border border-(--color-border-default) bg-(--color-bg-card) object-contain p-1 shadow-(--shadow-sm)"
              />
            ) : (
              <div
                className="grid h-10 w-10 place-items-center rounded-lg text-base font-semibold text-slate-900 shadow-(--shadow-sm)"
                style={{ background: homeSettings.primaryColor || 'var(--theme-color-primary, #047857)' }}
              >
                {shopName.trim().slice(0, 2).toUpperCase()}
              </div>
            )}
            <h2 className="text-2xl font-bold tracking-tight text-(--color-text-primary)">{shopName}</h2>
          </a>

          {homeSettings.footerNote && (
            <p className="max-w-xl text-sm leading-relaxed text-(--color-text-secondary)">
              {homeSettings.footerNote}
            </p>
          )}

          {(homeSettings.contactEmail || homeSettings.contactPhone) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-(--color-text-secondary)">
              {homeSettings.contactEmail ? (
                <a
                  href={`mailto:${homeSettings.contactEmail}`}
                  className="ca-theme-transition font-medium hover:text-(--color-text-primary)"
                >
                  {homeSettings.contactEmail}
                </a>
              ) : null}
              {homeSettings.contactPhone ? (
                <a
                  href={`tel:${homeSettings.contactPhone}`}
                  className="ca-theme-transition font-medium hover:text-(--color-text-primary)"
                >
                  {homeSettings.contactPhone}
                </a>
              ) : null}
            </div>
          )}
        </section>

        <section className="flex min-w-0 w-full flex-col items-start self-start text-left">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-(--color-text-muted)">
            {language === 'en' ? 'Links' : 'Link utili'}
          </p>
          {homeSettings.footerLinks.length > 0 ? (
            <ul className="mt-0.5 w-full list-none space-y-2 p-0">
              {homeSettings.footerLinks.map((link) => (
                <li key={`footer-${link.label}-${link.href}`} className="m-0 list-none p-0">
                  <a
                    href={isExternalHref(link.href) ? link.href : toLocalizedPath(link.href)}
                    target={isExternalHref(link.href) ? '_blank' : undefined}
                    rel={isExternalHref(link.href) ? 'noreferrer noopener' : undefined}
                    className="ca-theme-transition inline-flex items-center gap-2 text-sm font-medium text-(--color-text-secondary) hover:text-(--color-text-primary)"
                  >
                    <span>{resolveLinkLabel(link, locale)}</span>
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M8 5l8 7-8 7" />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        <section className="flex min-w-0 w-full flex-col items-start self-start text-left">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-(--color-text-muted)">
            Social
          </p>
          {homeSettings.socialLinks.length > 0 ? (
            <div className="m-0 mt-0.5 flex w-full items-center justify-start gap-2 p-0">
              {homeSettings.socialLinks.map((link) => {
                const normalizedLabel = normalizeSocialLabel(link.label)
                const hoverColorClass = normalizedLabel.includes('facebook')
                  ? 'hover:text-[#1877f2]'
                  : normalizedLabel.includes('instagram')
                    ? 'hover:text-[#e1306c]'
                    : 'hover:text-(--color-text-primary)'

                return (
                  <a
                    key={`social-${link.label}-${link.href}`}
                    href={link.href}
                    target={isExternalHref(link.href) ? '_blank' : undefined}
                    rel={isExternalHref(link.href) ? 'noreferrer noopener' : undefined}
                    className={`ca-theme-transition m-0 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-(--color-border-default) bg-(--color-bg-card) p-0 text-(--color-text-secondary) hover:border-(--color-border-strong) ${hoverColorClass}`}
                    title={resolveLinkLabel(link, locale)}
                    aria-label={resolveLinkLabel(link, locale)}
                  >
                    <SocialIcon label={link.label} />
                  </a>
                )
              })}
            </div>
          ) : null}
        </section>

      </div>

      <div className="ca-theme-transition border-t border-(--color-border-default)">
        <div className="mx-auto w-full max-w-[1740px] px-4 py-2.5 sm:px-6 lg:px-10">
          <p className="text-xs text-(--color-text-muted)">
            © {currentYear} {shopName}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default HomeFallbackFooter
